import { Button, makeStyles } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useHistory, useParams } from 'react-router-dom';
import { useWindowSize } from 'usehooks-ts';
import { InputField } from '../../components/ui/InputField';
import { Logo } from '../../components/ui/Logo';
import { USER_GROUP_EATER, USER_GROUP_EATER_GROUP_ADMIN } from '../../constants/constants';
import { IServiceUserContent, IServiceUserInfo } from '../../datasolvr/ServicesFacade';
import { IOnboardingUserInfo, ISize } from '../../interface/interface';
import { planEatApi } from '../../planEatApi/PlanEatApi';
import { IEaterGroupContent } from '../../planEatApi/PlanEatFacade';
import { PasswordRequirements } from './components/PasswordRequirements';

const useStyles = makeStyles((theme: any) => ({
    container: {
        display: "flex",
        width: "100%",
        justifyContent: "center",
        alignItems: "center",
        flexDirection: "column",
        marginTop: 25,
        marginBottom: 60,
        rowGap: 25,

        "& > *": {
            width: "90%",
            maxWidth: 800,
        }
    },
    topRow: {
        marginTop: 25,
        display: "flex",
        justifyContent: "center",
        flexDirection: "column",
        alignItems: "center"
    },
    inputContainer: {
        display: "grid",
        gridTemplateColumns: "50% auto",
        alignItems: "start",
        gap: 15,
        marginTop: 25,
        marginBottom: 25,
        width: "100%",

        [theme.breakpoints.down('md')]: {
            gridTemplateColumns: "auto",
        },
    }
}));

interface Props {

}

export const OnboardingVerification: React.FC<Props> = ({ }) => {
    const classes = useStyles()

    // Routing 
    let history = useHistory();
    const { email, token } = useParams<{ email?: string, token?: string }>();

    // State
    const [isCreatingEaterGroup, setIsCreatingEaterGroup] = useState(false)
    const size: ISize = useWindowSize();
    const [userChecked, setUserChecked] = useState(false)
    const [serviceUser, setServiceUser] = useState<IServiceUserInfo>();
    const [userInfo, setUserInfo] = useState<IOnboardingUserInfo>({
        name: "",
        password: "",
        confirmPassword: ""
    })
    const [eaterGroupContent, setEaterGroupContent] = useState<IEaterGroupContent>({
        id: "",
        name: "",
        description: "",
        officeId: "",
        eaterGroupName: "",
        eaterGroupCVR: "",
        eaterGroupAddress: "",
        eaterGroupPhoneNumber: "",
        eaterGroupAdminIds: [],
    });

    useEffect(() => {
        if (email && token) {
            planEatApi.postLogin({ userId: email, password: token })
                .then(response => {
                    localStorage.setItem("token", response.data.token)
                    getUser(email)
                })
                .catch((e) => {
                    localStorage.clear()
                    history.push("/")
                })
        } else {
            history.push("/")
        }
    }, [])

    function getUser(userId: string) {
        planEatApi.getServiceUser(userId)
            .then(response => {
                // Set current name
                setServiceUser(response.data)
                setUserInfo({
                    name: response.data.content.name,
                    password: "",
                    confirmPassword: ""
                })

                // Verify user
                verifyUser(response.data)

                // Check if user is eater group admin, and if then user is creating eater group
                if (response.data.content.data_raw?.groups) {
                    const userGroups = response.data.content.data_raw?.groups
                    if (userGroups.includes(USER_GROUP_EATER_GROUP_ADMIN)) {
                        setIsCreatingEaterGroup(true)
                        getEaterGroup(userId)
                    } else if (userGroups.includes(USER_GROUP_EATER)) {
                        getEaterGroup(userId)
                    } else {
                        localStorage.clear()
                        history.push("/")
                    }
                }
            })
            .catch((e) => {
                console.log(e);
            })
    }

    function getEaterGroup(applicationUserId: string) {
        planEatApi.getUsers()
            .then(response => {
                const user = response.data.find(item => item.content.applicationUserId === applicationUserId)
                if (user) {
                    planEatApi.getEaterGroup(user.content.eaterGroupId)
                        .then((response) => {
                            setEaterGroupContent(response.data.content)
                            setUserChecked(true)
                        })
                        .catch(e => {
                            console.log(e);
                        })
                }
            })
    }

    function verifyUser(tmpServiceUser: IServiceUserInfo){
        if(tmpServiceUser.content.data_raw?.options){
            if(tmpServiceUser.content.data_raw.options.emailVerificationToken){
                const email = tmpServiceUser.content.id
                const emailVerificationToken = tmpServiceUser.content.data_raw.options.emailVerificationToken
                planEatApi.postVerifyEmail(email,emailVerificationToken)
                .then((response) => {
                    toast.success("Verified user successfully")
                })
                .catch((e) => {
                    toast.error("Failed verifying user")
                    history.push("/")
                })
            }
        }
    }

    function postEaterGroup() {
        planEatApi.postEaterGroup(eaterGroupContent)
            .then(response => {
                postServiceUser()
            })
            .catch((e) => {
                console.log(e);
            })

    }

    function postServiceUser() {
        if (serviceUser && serviceUser.content.data_raw?.hash) {
            if (userInfo.password.length == 0) {
                toast.error("Please select a password")
            } else if (userInfo.password !== userInfo.confirmPassword) {
                toast.error("Passwords didn't match")
            } else {
                const updatedUser: IServiceUserContent = serviceUser?.content
                if (updatedUser.data_raw?.hash) {
                    updatedUser.name = userInfo.name
                    updatedUser.data_raw.hash = userInfo.password
                    updatedUser.data_raw.options = {} // Remove verification Token

                    planEatApi.postServiceUser(updatedUser)
                        .then((response) => {
                            planEatApi.postLogin({ userId: updatedUser.id, password: userInfo.password })
                            .then(response => {
                                toast.success("Successfull created user")
                                localStorage.setItem("token", response.data.token)
                                history.push("/booking")
                            })
                            .catch((e) => {
                                toast.error("Failed creating user")
                                localStorage.clear()
                                history.push("/")
                            })
                        })
                        .catch(e => {
                            if (e.response.data.content.length > 0) {
                                toast.error(e.response.data.content[e.response.data.content.length - 1].message)
                            } else {
                                toast.error("Failed creating user")
                            }
                        })
                }
            }
        }
    }

    const handleEaterGroupChange = (prop: keyof IEaterGroupContent) => (event: React.ChangeEvent<HTMLInputElement>) => {
        setEaterGroupContent({ ...eaterGroupContent, [prop]: event.target.value });
    };

    const handleUserInfoChange = (prop: keyof IOnboardingUserInfo) => (event: React.ChangeEvent<HTMLInputElement>) => {
        setUserInfo({ ...userInfo, [prop]: event.target.value });
    };

    return (
        <div >
            <div className={classes.topRow}>
                <Logo variant='normal' />
            </div>
            {
                userChecked ?
                    <div className={classes.container}>
                        <div style={{marginBottom: -15}}>
                            {
                                userChecked && eaterGroupContent && isCreatingEaterGroup ?
                                    <h4>
                                        Create Company
                                    </h4>
                                    :
                                    <h4>
                                        Join {eaterGroupContent.eaterGroupName}
                                    </h4>
                            }
                        </div>
                        {
                            isCreatingEaterGroup &&
                            <div>
                                <div>
                                    <h5>Company information</h5>
                                    <div className={classes.inputContainer}>
                                        <InputField
                                            fullWidth
                                            variant='outlined'
                                            id="groupCompany"
                                            label="Company name"
                                            name="groupCompany"
                                            value={eaterGroupContent.eaterGroupName}
                                            onChange={handleEaterGroupChange('eaterGroupName')}
                                            InputLabelProps={{
                                                shrink: true,
                                            }}
                                            required />
                                        <InputField
                                            fullWidth
                                            id="groupCvr"
                                            label="Company CVR"
                                            name="groupCvr"
                                            autoComplete="groupCvr"
                                            variant="outlined"
                                            helperText='If you don´t have a CVR number, stay blank'
                                            value={eaterGroupContent.eaterGroupCVR}
                                            onChange={handleEaterGroupChange('eaterGroupCVR')}
                                            InputLabelProps={{
                                                shrink: true,
                                            }}
                                            required />
                                        <InputField
                                            fullWidth
                                            id="groupAddress"
                                            label="Company Address"
                                            name="groupAddress"
                                            autoComplete="groupAddress"
                                            variant="outlined"
                                            value={eaterGroupContent.eaterGroupAddress}
                                            onChange={handleEaterGroupChange('eaterGroupAddress')}
                                            InputLabelProps={{
                                                shrink: true,
                                            }}
                                            required />
                                        <InputField
                                            fullWidth
                                            id="groupPhoneNumber"
                                            label="Eater Group Phone Number"
                                            name="groupPhoneNumber"
                                            autoComplete="groupPhoneNumber"
                                            variant="outlined"
                                            value={eaterGroupContent.eaterGroupPhoneNumber}
                                            onChange={handleEaterGroupChange('eaterGroupPhoneNumber')}
                                            InputLabelProps={{
                                                shrink: true,
                                            }}
                                            required />
                                    </div>
                                </div>

                            </div>

                        }

                        <div>
                            <h5>Personal information</h5>
                            <div className={classes.inputContainer}>
                                <InputField
                                    fullWidth
                                    id="name"
                                    label="Your name"
                                    name="name"
                                    autoComplete="name"
                                    variant="outlined"
                                    value={userInfo.name}
                                    onChange={handleUserInfoChange('name')}
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    required />
                                <div hidden={(size.width !== undefined && size.width < 900)} />
                                <InputField
                                    fullWidth
                                    id="password"
                                    label="Password"
                                    name="password"
                                    autoComplete="password"
                                    variant="outlined"
                                    type="password"
                                    value={userInfo.password}
                                    onChange={handleUserInfoChange('password')}
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    required />
                                <InputField
                                    fullWidth
                                    id="confirmPassword"
                                    label="Confirm password"
                                    name="confirmPassword"
                                    autoComplete="confirmPassword"
                                    variant="outlined"
                                    type="password"
                                    value={userInfo.confirmPassword}
                                    onChange={handleUserInfoChange('confirmPassword')}
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    required />
                            </div>
                            <div>
                                <PasswordRequirements
                                    password={userInfo.password}
                                    confirmPassword={userInfo.confirmPassword} />
                            </div>
                        </div>
                        <Button
                            variant='contained'
                            onClick={() => { isCreatingEaterGroup ? postEaterGroup() : postServiceUser() }}
                        >
                            {isCreatingEaterGroup ? "Create Company" : "Join " + eaterGroupContent.eaterGroupName}
                        </Button>
                    </div>
                    :
                    <div className={classes.container}>
                        <Skeleton variant="rect" width={"100%"} height={300} />
                        <Skeleton variant="rect" width={"100%"} height={300} />
                        <Skeleton variant="rect" width={"100%"} height={50} />
                    </div>
            }
        </div>
    )
}